import { FC } from 'react'
import TextField from '@mui/material/TextField'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import ClearIcon from '@mui/icons-material/Clear'
import { useFormikContext } from 'formik'
import DialogUploadValues from '@/types/dialogUploadValues'
import { MAX_DOSSIER_NAME_LENGTH } from '@/constants/NameLengths'
import { getFieldTestId } from '@/utils'
import useTranslate from '@/hooks/use-translate'
import { NfField } from '../primitives/NfField'
import { getNf } from '../nfTokens'

/**
 * Nom du dossier — requis, pré-rempli avec le nom du premier fichier.
 */
export const FolderNameField: FC = () => {
  const formik = useFormikContext<DialogUploadValues>()
  const { t: tCommon } = useTranslate('common')
  const { t: tUpload } = useTranslate('upload')

  const hasError =
    (formik.values.files.length > 0 && Boolean(formik.errors.recordName)) ||
    (formik.touched.recordName && Boolean(formik.errors.recordName))
  const errorMessage =
    (formik.values.files.length > 0 && formik.errors.recordName) ||
    (formik.touched.recordName && formik.errors.recordName)
  const value = formik.values.recordName ?? ''

  const firstFileName = formik.values.files[0]?.name
  const suggestedName = firstFileName
    ? firstFileName.split('.').slice(0, -1).join('.').slice(0, MAX_DOSSIER_NAME_LENGTH)
    : ''
  const isSuggestedValue = !!suggestedName && value === suggestedName

  return (
    <NfField
      label={tCommon('fileName')}
      required
      error={errorMessage || null}
      tooltip={isSuggestedValue ? tUpload('wizardFolderNameSuggested') : null}
    >
      <TextField
        fullWidth
        variant="outlined"
        placeholder={tUpload('wizardFolderNamePlaceholder')}
        inputProps={{
          ...getFieldTestId('recordName'),
          maxLength: MAX_DOSSIER_NAME_LENGTH,
        }}
        error={hasError}
        {...formik.getFieldProps('recordName')}
        InputProps={{
          endAdornment: (
            <>
              <Typography
                sx={(theme) => ({
                  fontSize: 10,
                  color: getNf(theme).muted2,
                  fontVariantNumeric: 'tabular-nums',
                  flexShrink: 0,
                })}
              >
                {value.length}/{MAX_DOSSIER_NAME_LENGTH}
              </Typography>
              <IconButton
                size="small"
                sx={{ visibility: value ? 'visible' : 'hidden' }}
                onClick={() => formik.setFieldValue('recordName', '')}
                data-testid="clear-button"
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </>
          ),
        }}
      />
    </NfField>
  )
}

export default FolderNameField
